import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, CreditCard, XCircle } from 'lucide-react';
import { fetchJson, formatMoney } from '@/lib/api';

/**
 * SelleCheck public payment page. The buyer opens the link the seller sent, sees
 * who is charging and for what, and pays by card on Stripe's page. No login.
 * Coming back from Stripe, the server confirms the payment with Stripe itself.
 */
export default function PayPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const volvio = !!params.get('session_id');
  const [pago, setPago] = useState(null);
  const [error, setError] = useState('');
  const [yendo, setYendo] = useState(false);
  const [sinRespuesta, setSinRespuesta] = useState(false);

  useEffect(() => {
    let vivo = true;
    let intentos = 0;
    let reloj;
    const leer = async () => {
      try {
        const d = await fetchJson(`/merchant-payments/public/${token}${volvio ? '/status' : ''}`);
        if (!vivo) return;
        setPago(d);
        setError('');
        // Al volver de Stripe la captura puede tardar unos segundos.
        if (volvio && ['checkout', 'capturing'].includes(d.status)) {
          if (intentos < 10) {
            intentos += 1;
            reloj = setTimeout(leer, 3000);
          } else {
            setSinRespuesta(true);
          }
        }
      } catch (e) {
        if (!vivo) return;
        // Un corte en el medio del sondeo no borra lo que ya se mostraba.
        if (volvio && intentos < 10 && e.status !== 404) {
          intentos += 1;
          reloj = setTimeout(leer, 3000);
        } else {
          setError(e.status === 404 ? 'This payment link is not valid.' : e.message);
        }
      }
    };
    leer();
    return () => { vivo = false; clearTimeout(reloj); };
  }, [token, volvio]);

  const pagar = async () => {
    setYendo(true);
    try {
      const d = await fetchJson(`/merchant-payments/public/${token}/checkout`, {
        method: 'POST', body: { origin_url: window.location.origin },
      });
      window.location.href = d.url;
    } catch (e) {
      setError(e.message);
      setYendo(false);
    }
  };

  const pagable = pago && (pago.status === 'open' || (pago.status === 'checkout' && !volvio));

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center px-4" data-testid="pay-page">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
        <p className="text-xs uppercase tracking-wide text-slate-500">SelleCheck · payment through SelleSend</p>
        {error && <p className="mt-4 text-sm text-red-600" data-testid="pay-error">{error}</p>}
        {!pago && !error && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>}
        {pago && (
          <>
            <h1 className="text-xl font-bold mt-1">{pago.seller_name || 'A SelleSend merchant'}</h1>
            <p className="text-slate-600 dark:text-slate-300 mt-2">{pago.description}</p>
            <p className="text-3xl font-bold tabular-nums mt-4" data-testid="pay-amount">
              {formatMoney(pago.amount)} <span className="text-sm font-normal text-slate-500">{(pago.currency || 'usd').toUpperCase()}</span>
            </p>

            {pago.status === 'paid' && (
              <p className="mt-6 flex items-center gap-2 text-emerald-700" data-testid="pay-done"><CheckCircle2 className="w-5 h-5" /> Paid. Thank you.</p>
            )}
            {['checkout', 'capturing'].includes(pago.status) && volvio && !sinRespuesta && (
              <p className="mt-6 flex items-center gap-2 text-slate-600"><Loader2 className="w-5 h-5 animate-spin" /> Confirming your payment with the bank…</p>
            )}
            {sinRespuesta && !['paid', 'open'].includes(pago.status) && (
              <p className="mt-6 text-sm text-slate-600" data-testid="pay-pending">
                Stripe has your payment and is still confirming it. You don't need to pay again; you can close this page or reload it in a few minutes.
              </p>
            )}
            {pago.status === 'canceled' && (
              <p className="mt-6 flex items-center gap-2 text-slate-600"><XCircle className="w-5 h-5" /> The seller canceled this payment.</p>
            )}
            {pagable && (
              <Button className="w-full mt-6" onClick={pagar} disabled={yendo} data-testid="pay-button">
                {yendo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />} Pay by card
              </Button>
            )}
            <p className="text-xs text-slate-500 mt-4">
              You pay on Stripe's secure page. The charge shows under the seller's name. SelleSend never sees your card number.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
